import { DeviceTypes } from "~/types/MFA.types";

/**
 * Detect where the request/browser is coming from based on user-agent.
 * NOTE: This intentionally preserves the existing heuristics used in `root.tsx`.
 */
export function getSourceFromUserAgent(userAgent: string | null | undefined) {
  const ua = userAgent || "";

  let source = DeviceTypes.WEB;
  if (ua.includes("_moneytor")) source = DeviceTypes.ANDROID;
  if (ua.includes("Version/8.0.2 Safari/600.2.5")) source = DeviceTypes.IOS;
  return source;
}

export function isMobileAppUserAgent(userAgent: string | null | undefined) {
  const source = getSourceFromUserAgent(userAgent);
  return source === DeviceTypes.IOS || source === DeviceTypes.ANDROID;
}

/**
 * Extract a stable device-type identifier from a user-agent string.
 * Used to scope WebAuthn credentials per device family so that registering
 * biometric on one device doesn't interfere with another.
 */
export function extractDeviceType(userAgent: string | null | undefined): string {
  const ua = userAgent || "";

  if (ua.includes("_moneytor")) return "android-app";
  if (ua.includes("Version/8.0.2 Safari/600.2.5")) return "ios-app";

  if (/iPhone/i.test(ua)) return "iphone";
  if (/iPad/i.test(ua)) return "ipad";
  if (/Android/i.test(ua)) return "android";
  if (/Macintosh/i.test(ua)) return "mac";
  if (/Windows/i.test(ua)) return "windows";
  if (/Linux/i.test(ua)) return "linux";

  return "unknown";
}

/**
 * Parse a user-agent string into a human-readable "Device - Browser" label
 * for display purposes (e.g. in credential management UI).
 */
export function parseUserAgentDisplay(userAgent: string | null | undefined): string {
  const ua = userAgent || "";

  if (ua.includes("_moneytor")) return "Android App";
  if (ua.includes("Version/8.0.2 Safari/600.2.5")) return "iOS App";

  let device = "Unknown";
  if (/iPhone/i.test(ua)) device = "iPhone";
  else if (/iPad/i.test(ua)) device = "iPad";
  else if (/Android/i.test(ua)) device = "Android";
  else if (/Macintosh/i.test(ua)) device = "Mac";
  else if (/Windows/i.test(ua)) device = "Windows";
  else if (/Linux/i.test(ua)) device = "Linux";

  let browser = "";
  if (/FxiOS\//i.test(ua)) browser = "Firefox";
  else if (/CriOS\//i.test(ua)) browser = "Chrome";
  else if (/Edg\//i.test(ua)) browser = "Edge";
  else if (/Firefox\//i.test(ua)) browser = "Firefox";
  else if (/Chrome\//i.test(ua) && !/Edg\//i.test(ua)) browser = "Chrome";
  else if (/Safari\//i.test(ua) && !/Chrome\//i.test(ua)) browser = "Safari";

  if (!browser) return device;
  return `${device} - ${browser}`;
}

